<svelte:options customElement={null} />

<span style="display: none;" bind:this={_resizeTarget}></span>

{#if intersecting && !!event && image_width > 0 && image_height > 0}
  {#await urls}
      <slot name="loading" style="position: absolute; top: 0; height: 0; z-index: 1;">
          <div class="image-spinner">
              <div class="lds-ring"><div></div><div></div><div></div><div></div></div>
          </div>
      </slot>
    {:then [src, srcset]}
        <img style={`width: ${image_width}px; height: ${image_height}px;`} {src} {srcset} alt={alt || _("webshop.event_image")} style:opacity={loaded ? 1 : 0} on:load={() => {loaded = true;}} on:loadstart={() => {loaded = false;}} start-ratio={""+initialRatio}>
        {#if !loaded}
          <slot name="loading" style="position: absolute; top: 0; height: 0; z-index: 1;">
          <div class="image-spinner" class:small={image_width < 70 || image_height < 70}>
                  <div class="lds-ring"><div></div><div></div><div></div><div></div></div>
              </div>
          </slot>
        {/if}
  {/await}
{/if}



<style lang="scss">
  :host {
    position: relative;
    display: grid;
    place-items: center;
  }

  .image-spinner {
      z-index: 1;
      background: var(--tm-event-image-loading-background, rgba(0,0,0,0));
      display: grid;
      place-items: center;
      width: 100%;
      height: 100%;
      position: absolute;

      --ring-width: 80px;
      --segment-padding: calc(var(--ring-width) / 10);


      &.small {
          --ring-width: 20px;
      }

      .lds-ring {
          display: inline-block;
          position: relative;
          width: var(--ring-width);
          height: var(--ring-width);
      }
      .lds-ring div {
          box-sizing: border-box;
          display: block;
          position: absolute;
          width: calc(var(--ring-width) - 2*var(--segment-padding));
          height: calc(var(--ring-width) - 2*var(--segment-padding));
          margin: var(--segment-padding);
          border: var(--segment-padding) solid #fff;
          border-radius: 50%;
          animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
          border-color: #fff transparent transparent transparent;
      }
      .lds-ring div:nth-child(1) {
          animation-delay: -0.45s;
      }
      .lds-ring div:nth-child(2) {
          animation-delay: -0.3s;
      }
      .lds-ring div:nth-child(3) {
          animation-delay: -0.15s;
      }
      @keyframes lds-ring {
          0% {
              transform: rotate(0deg);
          }
          100% {
              transform: rotate(360deg);
          }
      }
  }
</style>


<script lang="ts">
  import { onMount } from "svelte";
  import Shop from "../api";
  import { Translator } from "../translations";
  import { combineUnsubscribers } from "../utils/signals";
  import { devicePixelRatio } from "../utils/css";

  export let event: string;
  export let scalings: string = "1,2,4";
  export let gravity: string = "centre";
  export let alt: string|null = null;
  let _: Translator<any> = () => ""; 

  $: eventId = Number(event);
  $: rawScalings = (scalings.split(",")||["1"]).map(Number);
  
  let _resizeTarget: HTMLElement;
  let image_width: number = 0;
  let image_height: number = 0;

  const initialRatio = window.devicePixelRatio;
  let ratio = initialRatio;

  const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length == 0) return;
      const entry = entries[0];
      image_width = entry.contentRect.width;
      image_height = entry.contentRect.height;
  });

  $: containerElement = !!_resizeTarget ? (_resizeTarget.getRootNode() as ShadowRoot).host : null;

  $: {
    resizeObserver.disconnect();
    if (containerElement !== null) {
        resizeObserver.observe(containerElement);
        const rect = containerElement.getBoundingClientRect();
        image_width = rect.width;
        image_height = rect.height;
    }
  }

  let intersecting = false;
  const intersectionObserver = new IntersectionObserver((entries) => {
    for (let entry of entries) {
      intersecting = entry.isIntersecting;
    }
  });

  $: {
    intersectionObserver.disconnect();
    if (containerElement !== null) {
        intersectionObserver.observe(containerElement);
    }
  }

  $: ratio_image_width = image_width / ratio;
  $: ratio_image_height = image_height / ratio;

  onMount(() => {
    return () => {
      resizeObserver.disconnect();
    }
  });

  onMount(() => {
    return combineUnsubscribers(
        Shop.current.translator.subscribe((u) => {_ = u;}),
        devicePixelRatio.subscribe((r) => { ratio = r; })
    );
  })


  // What are we doing here?
  // We don't want too many versions of the image flying around in our CDN,
  // so we define specific heights that we will request.
  const HEIGHTS = [
      100,
      200,
      400,
      800,
     1000,
     1200,
     1800,
     3200,
     4000
  ];
  const imageRound = (n: number) => ((h) => h + (h%2))(Math.round(n))
  function findNearestSize(width: number, height: number): [number, number] {
    const optionHeight = HEIGHTS.find(h => h>height) || height;
    if (optionHeight === height) return [width, height];
    const newHeight = width / height * optionHeight
    return [
      imageRound(newHeight), imageRound(optionHeight)
    ];
  }

  $: unscaled = Shop.current.connector.getEventImageUrl(eventId, ...findNearestSize(ratio_image_width, ratio_image_height), gravity);
  $: scaled = Promise.all(rawScalings.map(s => Shop.current.connector.getEventImageUrl(eventId, ...findNearestSize(image_width*s, image_height*s), gravity).then((u) => `${u} ${s}x`))).then(all => all.join(", "));
  $: urls = Promise.all([unscaled, scaled]).catch(Shop.current.onError);

  let loaded = false;
</script>
